/**
 * Frame pipeline: what `requestVideoFrameCallback` says about each presented frame, reduced
 * to a per-tick summary; which latency source a guest engine can supply; and which congestion
 * signals an engine may fire at all.
 *
 * The decomposition is the instrument that separates the two standing-buffer hypotheses:
 * time spent between receive and present is buffer plus decode; `processingDuration` is the
 * decode alone; present-to-display is the compositor. Where the standing frames sit is
 * whichever of those grows.
 *
 * Browser-free and Node/Jest-safe: types, constants and one pure lookup.
 */

import type { GuestBrowserCapability } from '../guestBrowserCapability';

/**
 * How a guest labels the latency samples it sends the host.
 *
 * - `rvfc-rtp`: rVFC exposes `rtpTimestamp`; the host correlates against its send record.
 * - `rvfc-capture-time`: rVFC exposes `captureTime`/`receiveTime` but no `rtpTimestamp`
 *   (WebKit); correlation uses the datachannel clock offset.
 * - `input-photon`: guest-clock-only measurement from a tagged input to its presented frame.
 */
export type LatencySampleSource = 'rvfc-rtp' | 'rvfc-capture-time' | 'input-photon';
export const LATENCY_SAMPLE_SOURCES: ReadonlyArray<LatencySampleSource> = [
  'rvfc-rtp',
  'rvfc-capture-time',
  'input-photon',
];

export function isLatencySampleSource(value: unknown): value is LatencySampleSource {
  return typeof value === 'string' && (LATENCY_SAMPLE_SOURCES as ReadonlyArray<string>).includes(value);
}

/** Per-tick reduction of rVFC metadata. Every duration is ms; percentiles are nearest-rank. */
export type FramePipelineSummary = {
  /** receiveTime -> presentationTime: jitter buffer plus decode. */
  receiveToPresentP50Ms?: number;
  receiveToPresentP95Ms?: number;
  /** processingDuration: the decode alone. */
  processingP50Ms?: number;
  /** expectedDisplayTime - presentationTime: the compositor. */
  presentToDisplayP50Ms?: number;
  sampleCount: number;
};

function isOptionalFiniteNumber(value: unknown): boolean {
  return value === undefined || (typeof value === 'number' && Number.isFinite(value));
}

export function isFramePipelineSummary(value: unknown): value is FramePipelineSummary {
  if (value === null || typeof value !== 'object') return false;
  const s = value as Record<string, unknown>;
  if (typeof s['sampleCount'] !== 'number' || !Number.isFinite(s['sampleCount']) || s['sampleCount'] < 0) {
    return false;
  }
  return (
    isOptionalFiniteNumber(s['receiveToPresentP50Ms']) &&
    isOptionalFiniteNumber(s['receiveToPresentP95Ms']) &&
    isOptionalFiniteNumber(s['processingP50Ms']) &&
    isOptionalFiniteNumber(s['presentToDisplayP50Ms'])
  );
}

/**
 * Which congestion signals an engine is allowed to produce a verdict from.
 *
 * A verdict citing a signal the engine cannot measure is a verdict built on a fabricated
 * number. Safari exposes no jitter-buffer counters, no framesReceived and no freezeCount, so
 * on Safari the playout trend, the decode backlog and the freeze delta must never decide
 * anything. It does expose totalDecodeTime, so decoder residency stands.
 */
export type CongestionSignalAvailability = {
  playoutTrend: boolean;
  decodeBacklog: boolean;
  freeze: boolean;
  decodeResidency: boolean;
};

export function signalAvailabilityFor(capability: GuestBrowserCapability): CongestionSignalAvailability {
  return {
    playoutTrend: capability.exposesJitterBufferDelay,
    decodeBacklog: capability.exposesFramesReceived,
    freeze: capability.exposesFreezeCount,
    decodeResidency: true,
  };
}
